/**
 * Decisions API
 *
 * Client for the FLOW-569 decision CRUD routes and FLOW-570's extraction /
 * quick-capture routes. Extraction and drafting both run an Anthropic round
 * trip on the backend, so — same as `breakdown.ts` — they share the chat
 * timeout rather than the default fetch behaviour. Everything else here is a
 * plain lookup or mutation and does not.
 */

import { api, ApiError } from "./client";
import type {
  CreateDecisionEdgeRequest,
  CreateDecisionRequest,
  Decision,
  DecisionEdge,
  DecisionId,
  DecisionListResponse,
  DecisionSearchParams,
  DecisionWorkItemLinkListResponse,
  DraftDecisionResponse,
  IntakeId,
  ProjectId,
  WorkItemDecisionLinkListResponse,
  WorkItemId,
} from "./types";

/** Matches the chat client: the backend's Anthropic timeout plus headroom */
const DECISION_TIMEOUT_MS = 130_000;

/** Run a request under the extended timeout, converting an abort into a 408. */
async function withTimeout<T>(
  run: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), DECISION_TIMEOUT_MS);

  try {
    return await run(controller.signal);
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new ApiError(408, "The request timed out. Please retry.");
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Extract decisions from a committed breakdown's session. Safe to call more
 * than once — a second call returns whatever the first one already produced
 * rather than re-running the model. 409s if the breakdown isn't committed.
 */
export async function extractDecisions(
  intakeId: IntakeId,
): Promise<DecisionListResponse> {
  return withTimeout((signal) =>
    api.post<DecisionListResponse>(
      `/intakes/${intakeId}/decisions/extract`,
      undefined,
      { signal },
    ),
  );
}

/** Fetch decisions already captured for an intake */
export async function getIntakeDecisions(
  intakeId: IntakeId,
): Promise<DecisionListResponse> {
  return api.get<DecisionListResponse>(`/intakes/${intakeId}/decisions`);
}

/** Draft a structured decision from free text. Persists nothing. */
export async function draftDecision(
  projectId: ProjectId,
  text: string,
): Promise<DraftDecisionResponse> {
  return withTimeout((signal) =>
    api.post<DraftDecisionResponse>(
      `/projects/${projectId}/decisions/draft`,
      { text },
      { signal },
    ),
  );
}

/**
 * Confirm a proposed decision: records who confirmed it and when, and moves
 * it to active.
 *
 * A dedicated endpoint rather than `PUT {status: "active"}` since FLOW-672 —
 * the backend refuses to move status into a confirmed value through the
 * generic update at all, so that the stamp and the status are always written
 * by the same, separately-gated action. 403s unless the caller is a project
 * owner and an admin of its org; `canConfirmDecisions` in `utils/permissions`
 * is the mirror of that gate.
 */
export async function confirmDecision(
  projectId: ProjectId,
  id: DecisionId,
): Promise<Decision> {
  return api.post<Decision>(
    `/projects/${projectId}/decisions/${id}/confirm`,
    undefined,
  );
}

/** Dismiss a proposed decision */
export async function dismissDecision(
  projectId: ProjectId,
  id: DecisionId,
): Promise<void> {
  return api.delete(`/projects/${projectId}/decisions/${id}`);
}

/** Save an ad-hoc decision drafted via quick capture */
export async function createDecision(
  projectId: ProjectId,
  body: CreateDecisionRequest,
): Promise<Decision> {
  return api.post<Decision>(`/projects/${projectId}/decisions`, body);
}

// ---------------------------------------------------------------------------
// Query interface (FLOW-572)
// ---------------------------------------------------------------------------

function toDecisionQueryString(params: DecisionSearchParams): string {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.category) search.set("category", params.category);
  if (params.tag) search.set("tag", params.tag);
  if (params.q) search.set("q", params.q);
  if (params.work_item_id) search.set("work_item_id", params.work_item_id);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

/** Keyword/filter search over a project's decisions */
export async function searchDecisions(
  projectId: ProjectId,
  params: DecisionSearchParams = {},
): Promise<DecisionListResponse> {
  return api.get<DecisionListResponse>(
    `/projects/${projectId}/decisions${toDecisionQueryString(params)}`,
  );
}

/** Fetch a single decision */
export async function getDecision(
  projectId: ProjectId,
  id: DecisionId,
): Promise<Decision> {
  return api.get<Decision>(`/projects/${projectId}/decisions/${id}`);
}

/** Decisions linked to a work item (FLOW-571) */
export async function listWorkItemDecisions(
  workItemId: WorkItemId,
): Promise<WorkItemDecisionLinkListResponse> {
  return api.get<WorkItemDecisionLinkListResponse>(
    `/work-items/${workItemId}/decisions`,
  );
}

/** Work items linked to a decision (FLOW-571) */
export async function listDecisionWorkItems(
  decisionId: DecisionId,
): Promise<DecisionWorkItemLinkListResponse> {
  return api.get<DecisionWorkItemLinkListResponse>(
    `/decisions/${decisionId}/work-items`,
  );
}

/**
 * Create a decision-to-decision edge (FLOW-569). Used by the conflict flow to
 * express what accepting a warning means: `supersedes` retires the older
 * decision (a DB trigger flips its status), `conflicts_with` records the
 * tension without resolving it.
 */
export async function createDecisionEdge(
  projectId: ProjectId,
  decisionId: DecisionId,
  body: CreateDecisionEdgeRequest,
): Promise<DecisionEdge> {
  return api.post<DecisionEdge>(
    `/projects/${projectId}/decisions/${decisionId}/edges`,
    body,
  );
}
